import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  BuyerResearchBriefDto,
  BuyerResearchCandidatePageDto,
  BuyerResearchCandidateRowDto,
  BuyerResearchDismissalDto,
  BuyerResearchMoneyField,
  BuyerResearchRefinementDto,
  BuyerResearchRefinementStateDto,
  BuyerResearchWithheldStateDto,
} from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import {
  Button,
  FilterChips,
  ListToolbar,
  Skeleton,
  SortSelect,
  StatusBadge,
  SubTable,
  SubTableFooter,
  SUB_TABLE_CHROME_THRESHOLD,
  Timestamp,
  useToast,
  type ActiveFilterChip,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { useListFilters } from "@/hooks/useListFilters";
import { useUserTimeZone } from "@/utils/datetime";
import { InlineQueryError } from "../queryStates";
import { MoneyRangeField } from "./MoneyRangeField";
import { ResearchCandidateDetailLoader, researchCandidateColumns } from "./ResearchCandidateCard";
import { researchCopy } from "./researchCopy";
import { countsHeader, refinementRefusalMessage } from "./researchFormat";
import {
  MONEY_BOUND_KEYS,
  MONEY_FIELDS,
  researchListFiltersFor,
  RESEARCH_PAGE_PARAM,
  RESEARCH_PAGE_SIZES,
  briefBoundsWrite,
  briefHasBounds,
  moneyFieldFiltered,
  researchFiltersActive,
  researchListQuery,
  type ResearchFitThreshold,
  type ResearchListFilters,
  type ResearchPageSize,
} from "./researchListFilters";
import {
  useClearRefinement,
  useResearchCandidatePage,
  useResearchDismissals,
  useRevealWithheldResearch,
  useUndismissCandidate,
} from "./useBuyerResearch";

const FIT_THRESHOLDS: readonly ResearchFitThreshold[] = ["any", "50", "75"];

/**
 * One run's candidates, as the SERVER filtered, sorted and paged them (R5),
 * plus the buyer's dismissal set behind a toggle.
 *
 * <p>🔴 <b>THE CLIENT DOES NOT FILTER, SORT OR PAGE.</b> Every control on this
 * surface writes a URL key (R20); the keys become the list request; the server
 * answers with one page and every count this surface prints. There is one
 * ordering, in the service — a second one here, even one that agreed today,
 * would be free to disagree the next time either moved.</p>
 *
 * <p>Dismissals are PER BUYER, not per run (D8): a listing dismissed on one
 * search stays dismissed on every later one, which is why the list under the
 * toggle is the buyer's whole set rather than this run's.</p>
 *
 * <p>Composition, top to bottom (recon §7): the refinement's own line first, so
 * it reads as the first narrowing; the filter bar; the count line; the lines
 * that say what a filter hid and could not judge; the table; the rows already
 * bought and not yet shown.</p>
 */
export function ResearchResults({
  buyerLeadId,
  runId,
  canEdit,
  refinement = null,
  withheld = null,
  brief = null,
  quick = false,
  alreadyTiedCount = 0,
  skippedAtSearch = 0,
  live = false,
}: {
  buyerLeadId: string;
  runId: string;
  canEdit: boolean;
  /**
   * Ask Phoenix state for this run, off the run read.
   * Null on a payload written before Ask Phoenix existed.
   */
  refinement?: BuyerResearchRefinementStateDto | null;
  /**
   * What this run bought, scored and did not show.
   *
   * ⚠️ Null is "the server did not evaluate it", NOT "there is nothing": only
   * the single-run read counts held-back rows, so the list read legitimately
   * sends null and the footer renders nothing rather than "0 more".
   */
  withheld?: BuyerResearchWithheldStateDto | null;
  /** The run's brief, for "Match the buyer's brief" (R16). */
  brief?: BuyerResearchBriefDto | null;
  /**
   * A typed Quick search (A7/M2): the fit chip defaults to Any, the counts line
   * is the server's quick counts, the hidden couldn't-confirm / didn't-match
   * rows sit behind their own links, and there is no brief to match.
   */
  quick?: boolean;
  /** The run's own finalize count of rows already tied to the buyer. */
  alreadyTiedCount?: number;
  /**
   * The run's finalize `dismissedCount`: rows skipped AT SEARCH because the
   * buyer had already dismissed them (R18). A different fact from the live
   * dismissed count the list reports, which is why the header names it
   * separately and only when it is above zero.
   */
  skippedAtSearch?: number;
  /**
   * RG23 — the run is still scoring (a first run, or a pull), so the page
   * re-reads every five seconds and each saved group of twenty lands on it.
   * The page, the filters and the sort are left where the reader put them; the
   * one reset to page 1 happens when a pull starts, not on each refresh.
   */
  live?: boolean;
}) {
  const copy = researchCopy.results;
  const filters = useListFilters(researchListFiltersFor(quick), { pageParam: RESEARCH_PAGE_PARAM });
  const { values } = filters;
  const list = useResearchCandidatePage(
    buyerLeadId,
    runId,
    researchListQuery(values, filters.page),
    true,
    live,
  );
  const [, setSearchParams] = useSearchParams();

  /**
   * One URL write for several keys. `filters.set` writes ONE key and each call
   * starts from the query string of the render it was made in, so two in a row
   * would overwrite each other. The page goes with every write here, as it does
   * with every filter write the hook makes: a new filter is a new result set.
   */
  const writeParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value == null) next.delete(key);
            else next.set(key, value);
          }
          next.delete(RESEARCH_PAGE_PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // 🔴 ONE ROW'S SCORING AT A TIME, and only for the page it was opened on. The
  // detail is two fit groups, the reasons, the contact block and the
  // provenance — open three and the table is the stack of cards it replaced.
  // The open row is scoped to the run, the filters and the page it was opened
  // under, so a filter or page change closes it without an effect to keep in
  // step: a row that is no longer on screen cannot stay "open".
  //
  // Presentation only: expanding touches no server state. It fetches the row's
  // detail, and nothing is written.
  const expandScope = `${runId}|${filters.resetToken}|${filters.page}`;
  const [expanded, setExpanded] = useState<{ id: string; scope: string } | null>(null);
  const expandedId = expanded?.scope === expandScope ? expanded.id : null;

  // 🔴 RF1 — a refinement is a LENS, never a re-order. What it hides is stated
  // above the list it hides rows from, because a list twenty-eight rows
  // shorter with nothing saying why is a list a specialist stops trusting.
  const lens = refinement?.active != null && refinement.active.status === "applied"
    ? refinement.active
    : null;

  const data = list.data ?? null;
  const filtersActive = researchFiltersActive(filters);
  // 🔴 §5 — PROGRESSIVE CHROME, KEYED ON THE RUN, NOT THE PAGE. The default
  // fit filter hides rows by itself, so keying on what came back would let a
  // nine-row result hide the very controls that could widen it. A filter the
  // reader has set keeps the bar on screen however little it leaves.
  const chrome = (data?.totalOnRun ?? 0) >= SUB_TABLE_CHROME_THRESHOLD || filtersActive;

  return (
    <div className="grid gap-space-3" data-testid="research-results">
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-space-3 gap-y-space-1">
          <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">
            {researchCopy.tab.resultsHeading}
          </h3>
          {data && quick && data.quickCounts && (
            <span className="text-caption text-gray-mid tabular-nums" data-testid="research-quick-counts">
              {researchCopy.quick.countsLine(data.quickCounts)}
            </span>
          )}
          {data && !(quick && data.quickCounts) && (
            <span className="text-caption text-gray-mid" data-testid="research-counts">
              {countsHeader({
                totalOnRun: data.totalOnRun,
                dismissedOnRun: data.dismissedOnRun,
                alreadyTiedCount,
                skippedAtSearch,
              })}
            </span>
          )}
        </div>
        <label className="inline-flex items-center gap-space-1 text-small text-onyx">
          <input
            type="checkbox"
            checked={values.rDismissed}
            onChange={(e) => filters.set("rDismissed", e.target.checked)}
            data-testid="research-show-dismissed"
          />
          {copy.showDismissed}
        </label>
        {quick && data && (
          <QuickRevealLinks
            hiddenUnconfirmed={data.hiddenUnconfirmed ?? 0}
            hiddenNoMatch={data.hiddenNoMatch ?? 0}
            showingUnconfirmed={values.rUnconfirmed}
            showingNoMatch={values.rNoMatch}
            onToggle={(key, on) => filters.set(key, on)}
          />
        )}
      </div>

      {lens && (
        <RefinementLens
          lens={lens}
          buyerLeadId={buyerLeadId}
          runId={runId}
          canEdit={canEdit}
        />
      )}

      {chrome && (
        <ResearchListToolbar
          filters={filters}
          data={data}
          brief={quick ? null : brief}
          onWrite={writeParams}
        />
      )}

      {list.isError && !data ? (
        <InlineQueryError
          what={copy.listLoadFailedWhat}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : !data ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <>
          {chrome && data.totalOnRun > 0 && (
            <p className="m-0 text-caption text-gray-mid tabular-nums" data-testid="research-count-line">
              {countLineFor(data)}
            </p>
          )}

          <HiddenLines data={data} filters={filters} />

          {data.items.length === 0 ? (
            <EmptyList
              data={data}
              quick={quick}
              lens={lens}
              filtersActive={filtersActive}
              onClear={filters.reset}
            />
          ) : (
            /*
              🔴 `chromeThreshold={Infinity}`: the table's OWN search, sort and
              pager would work on the twenty-five rows it was handed and call
              that the list. This page's chrome is the toolbar above, which asks
              the server. No column carries a `sortAccessor` either (see
              `researchCandidateColumns`), so a header click could not sort a
              page in the browser even if the threshold moved.

              `table-fixed` is set on the table itself through the wrapper,
              because `SubTable` passes its `className` to its root rather than
              to the table — and the column widths assume it (§11).
            */
            <SubTable<BuyerResearchCandidateRowDto>
              caption={researchCopy.tab.resultsHeading}
              data-testid="research-candidates"
              className="[&>div>div>table]:table-fixed"
              rows={data.items}
              rowKey={(c) => c.id}
              rowTestId={() => "research-candidate"}
              rowSelected={(c) => c.id === expandedId}
              columns={researchCandidateColumns({
                buyerLeadId,
                runId,
                canEdit,
                expandedId,
                onToggleExpanded: (id) =>
                  setExpanded((prev) =>
                    prev?.id === id && prev.scope === expandScope ? null : { id, scope: expandScope },
                  ),
                latestBatch: data.latestBatch,
                batchCount: data.batchCount,
                quick,
              })}
              chromeThreshold={Number.POSITIVE_INFINITY}
              renderExpanded={(c) =>
                c.id === expandedId ? (
                  <ResearchCandidateDetailLoader
                    buyerLeadId={buyerLeadId}
                    runId={runId}
                    candidateId={c.id}
                    canEdit={canEdit}
                    quick={quick}
                  />
                ) : null
              }
            />
          )}

          {chrome && data.totalMatching > 0 && (
            <ResearchListFooter data={data} filters={filters} />
          )}
        </>
      )}

      {/* 🔴 ABOVE THE PULL CONTROL AND AT THE FOOT OF THIS LIST: what is
          already bought is offered before what has to be bought. A specialist
          should reach the end of what the vendor already charged for before
          they reach the vendor again. */}
      <WithheldFooter
        buyerLeadId={buyerLeadId}
        runId={runId}
        withheld={withheld}
        canEdit={canEdit}
      />

      {values.rDismissed && <DismissalList buyerLeadId={buyerLeadId} canEdit={canEdit} />}
    </div>
  );
}

/**
 * Q1/Q2 — the rows a quick search could not confirm, or that did not match
 * what was typed, are left off the default list and offered here, each with
 * the server's own count. Each is a URL toggle (`rUnconfirmed` / `rNoMatch`),
 * so a reader's choice survives a refresh and a shared link.
 */
function QuickRevealLinks({
  hiddenUnconfirmed,
  hiddenNoMatch,
  showingUnconfirmed,
  showingNoMatch,
  onToggle,
}: {
  hiddenUnconfirmed: number;
  hiddenNoMatch: number;
  showingUnconfirmed: boolean;
  showingNoMatch: boolean;
  onToggle: (key: "rUnconfirmed" | "rNoMatch", on: boolean) => void;
}) {
  const copy = researchCopy.quick;
  return (
    <div className="flex flex-wrap items-center gap-space-2" data-testid="research-quick-reveal">
      {(showingUnconfirmed || hiddenUnconfirmed > 0) && (
        <Button
          type="button"
          variant="text"
          size="sm"
          aria-pressed={showingUnconfirmed}
          onClick={() => onToggle("rUnconfirmed", !showingUnconfirmed)}
          data-testid="research-show-unconfirmed"
        >
          {showingUnconfirmed ? copy.hideUnconfirmed : copy.showUnconfirmed(hiddenUnconfirmed)}
        </Button>
      )}
      {(showingNoMatch || hiddenNoMatch > 0) && (
        <Button
          type="button"
          variant="text"
          size="sm"
          aria-pressed={showingNoMatch}
          onClick={() => onToggle("rNoMatch", !showingNoMatch)}
          data-testid="research-show-no-match"
        >
          {showingNoMatch ? copy.hideNoMatch : copy.showNoMatch(hiddenNoMatch)}
        </Button>
      )}
    </div>
  );
}

/** "Showing 1–25 of 37 matching · 94 on this run", off the page the server served. */
function countLineFor(data: BuyerResearchCandidatePageDto): string {
  const first = data.totalMatching === 0 ? 0 : (data.page - 1) * data.pageSize + 1;
  const last = data.totalMatching === 0 ? 0 : first + data.items.length - 1;
  return researchCopy.results.countLine(first, last, data.totalMatching, data.totalOnRun);
}

/**
 * The filter bar: search, fit, the latest pull, money ranges behind "More
 * filters", the brief button, and the sort.
 *
 * <p>🔴 <b>SORT IS ONE CONTROL FOR KEY AND DIRECTION.</b> Best fit has no
 * direction — it is one fixed order on the server (R3) — so it is a single
 * option rather than a pair whose second half would do nothing.</p>
 */
function ResearchListToolbar({
  filters,
  data,
  brief,
  onWrite,
}: {
  filters: ResearchListFilters;
  data: BuyerResearchCandidatePageDto | null;
  brief: BuyerResearchBriefDto | null;
  onWrite: (updates: Record<string, string | null>) => void;
}) {
  const copy = researchCopy.results;
  const { values, inputs } = filters;

  const sortToken = values.rSort === "fit" ? "fit" : `${values.rSort}:${values.rDir}`;
  const sortOptions = Object.entries(copy.sortOptions).map(([value, label]) => ({ value, label }));

  const moneyActive = MONEY_FIELDS.filter((f) => moneyFieldFiltered(values, f));
  const chips: ActiveFilterChip[] = [
    ...moneyActive.map((field) => {
      const keys = MONEY_BOUND_KEYS[field];
      const min = values[keys.min];
      const max = values[keys.max];
      return {
        key: `money-${field}`,
        label: copy.moneyChip(
          copy.moneyFields[field],
          min == null ? null : formatCurrency(min),
          max == null ? null : formatCurrency(max),
        ),
        onRemove: () => onWrite({ [keys.min]: null, [keys.max]: null }),
      };
    }),
    ...values.rMissing.map((field) => ({
      key: `missing-${field}`,
      label: copy.missingChip(copy.moneyFields[field]),
      onRemove: () => filters.set("rMissing", values.rMissing.filter((f) => f !== field)),
    })),
  ];

  const clearHidden = () => {
    const updates: Record<string, string | null> = { rMissing: null };
    for (const field of MONEY_FIELDS) {
      updates[MONEY_BOUND_KEYS[field].min] = null;
      updates[MONEY_BOUND_KEYS[field].max] = null;
    }
    onWrite(updates);
  };

  return (
    <ListToolbar
      data-testid="research-toolbar"
      search={{
        value: inputs.rQ,
        onChange: (v) => filters.set("rQ", v),
        placeholder: copy.searchPlaceholder,
        label: copy.searchLabel,
      }}
      sort={
        <SortSelect
          value={sortToken}
          onChange={(token) => {
            if (token === "fit") {
              onWrite({ rSort: null, rDir: null });
              return;
            }
            const [key, dir] = token.split(":");
            onWrite({ rSort: key, rDir: dir === "asc" ? "asc" : null });
          }}
          options={sortOptions}
          label={copy.sortLabel}
        />
      }
      primary={
        <div className="flex flex-wrap items-center gap-space-3">
          <FilterChips
            chips={FIT_THRESHOLDS.map((id) => ({ id, label: copy.fitChips[id] }))}
            activeId={values.rFit}
            onChange={(id) => filters.set("rFit", id as ResearchFitThreshold)}
            label={copy.fitLabel}
          />
          {/* Only on a pulled run: a single search has no "latest pull". */}
          {data != null && data.batchCount >= 2 && (
            <FilterChips
              chips={[{ id: "latest", label: copy.latestOnly }]}
              activeId={values.rLatest ? "latest" : ""}
              onChange={() => filters.set("rLatest", !values.rLatest)}
              label={copy.latestOnly}
            />
          )}
          {/* D5 — every run kind, off by default; kept in the page link. */}
          <FilterChips
            chips={[{ id: "hideAttached", label: copy.hideAttached }]}
            activeId={values.rHideAttached ? "hideAttached" : ""}
            onChange={() => filters.set("rHideAttached", !values.rHideAttached)}
            label={copy.hideAttached}
          />
        </div>
      }
      moreFilters={{
        activeCount: moneyActive.length,
        content: (
          <div className="grid gap-space-3">
            {MONEY_FIELDS.map((field) => {
              const keys = MONEY_BOUND_KEYS[field];
              return (
                <MoneyRangeField
                  key={field}
                  label={copy.moneyFields[field]}
                  min={inputs[keys.min]}
                  max={inputs[keys.max]}
                  onMinChange={(v) => filters.set(keys.min, v)}
                  onMaxChange={(v) => filters.set(keys.max, v)}
                  testId={`research-money-${field}`}
                />
              );
            })}
          </div>
        ),
      }}
      actions={
        brief && briefHasBounds(brief) ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onWrite(briefBoundsWrite(brief))}
            data-testid="research-match-brief"
          >
            {copy.matchBrief}
          </Button>
        ) : undefined
      }
      activeChips={chips}
      onClearAllChips={clearHidden}
    />
  );
}

/**
 * What a filter hid and could not judge, and the one click that brings it back.
 *
 * <p>🔴 <b>ONLY WHAT THE FILTER COULD NOT JUDGE.</b> A row with a real figure
 * outside the range is not a match and is counted nowhere; a row with no figure
 * — or a figure under $1,000, which is what "looks wrong" means (R10) — was
 * hidden for want of one, and "Show them" keeps those rows for that field.</p>
 *
 * <p>Rendered whether or not the bar is: on a short run with no chrome, the
 * fit line is the ONLY way back to the rows the default threshold hid.</p>
 */
function HiddenLines({
  data,
  filters,
}: {
  data: BuyerResearchCandidatePageDto;
  filters: ResearchListFilters;
}) {
  const copy = researchCopy.results;
  const { values } = filters;

  const fieldLines = MONEY_FIELDS.map((field) => ({
    field,
    missing: data.hiddenForMissing[field],
    implausible: data.hiddenForImplausible[field],
  })).filter(
    (l) =>
      moneyFieldFiltered(values, l.field) &&
      !values.rMissing.includes(l.field) &&
      l.missing + l.implausible > 0,
  );
  const showBelowFit = values.rFit !== "any" && data.belowFit > 0;

  if (fieldLines.length === 0 && !showBelowFit) return null;

  return (
    <div className="grid gap-[2px]" data-testid="research-hidden-lines">
      {fieldLines.map((l) => (
        <HiddenLine
          key={l.field}
          text={copy.hiddenForField(copy.moneyFields[l.field], l.missing, l.implausible)}
          action={copy.showThem}
          onAction={() => filters.set("rMissing", addField(values.rMissing, l.field))}
          testId={`research-hidden-${l.field}`}
        />
      ))}
      {showBelowFit && (
        <HiddenLine
          text={copy.belowFit(data.belowFit)}
          action={copy.showAllFit}
          onAction={() => filters.set("rFit", "any")}
          testId="research-below-fit"
        />
      )}
    </div>
  );
}

function addField(
  fields: readonly BuyerResearchMoneyField[],
  field: BuyerResearchMoneyField,
): readonly BuyerResearchMoneyField[] {
  return fields.includes(field) ? fields : [...fields, field];
}

function HiddenLine({
  text,
  action,
  onAction,
  testId,
}: {
  text: string;
  action: string;
  onAction: () => void;
  testId: string;
}) {
  return (
    <p className="m-0 text-caption text-gray-mid break-words" data-testid={testId}>
      {text} ·{" "}
      <Button type="button" variant="text" size="sm" onClick={onAction} data-testid={`${testId}-show`}>
        {action}
      </Button>
    </p>
  );
}

/** The three empty states, each saying something true about why. */
function EmptyList({
  data,
  quick = false,
  lens,
  filtersActive,
  onClear,
}: {
  data: BuyerResearchCandidatePageDto;
  quick?: boolean;
  lens: BuyerResearchRefinementDto | null;
  filtersActive: boolean;
  onClear: () => void;
}) {
  const copy = researchCopy.results;
  return (
    <div className="rounded-lg border border-border-light bg-white-pure p-space-4" data-testid="research-empty">
      {data.totalOnRun === 0 ? (
        <>
          <p className="m-0 text-small text-onyx">{researchCopy.tab.noCandidates}</p>
          <p className="m-0 mt-space-1 text-caption text-gray-mid">{researchCopy.tab.noCandidatesHint}</p>
        </>
      ) : quick && !filtersActive && (data.hiddenUnconfirmed ?? 0) + (data.hiddenNoMatch ?? 0) > 0 ? (
        <>
          <p className="m-0 text-small text-onyx" data-testid="research-quick-no-matches">
            {researchCopy.quick.noMatches}
          </p>
          <p className="m-0 mt-space-1 text-caption text-gray-mid">{researchCopy.quick.noMatchesHint}</p>
        </>
      ) : lens != null && lens.hiddenCount > 0 && lens.visibleCount === 0 ? (
        // 🔴 A REFINEMENT THAT MATCHED NOTHING IS NOT AN EMPTY RUN: these
        // listings were found and shown, and one question narrowed them to
        // nothing. The way back is the Show all in the refinement's own line.
        <>
          <p className="m-0 text-small text-onyx" data-testid="research-refinement-empty">
            {copy.refinementMatchedNothing(lens.text)}
          </p>
          <p className="m-0 mt-space-1 text-caption text-gray-mid">{copy.refinementMatchedNothingHint}</p>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-space-2">
          <p className="m-0 text-small text-onyx" data-testid="research-filtered-empty">
            {copy.filteredEmpty}
          </p>
          {filtersActive && (
            <Button size="sm" variant="secondary" onClick={onClear} data-testid="research-clear-filters">
              {copy.clearFilters}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** §10 — the page counter, Previous/Next, and the page size (R12). */
function ResearchListFooter({
  data,
  filters,
}: {
  data: BuyerResearchCandidatePageDto;
  filters: ResearchListFilters;
}) {
  const first = (data.page - 1) * data.pageSize + 1;
  const last = first + data.items.length - 1;
  const totalPages = Math.max(1, Math.ceil(data.totalMatching / data.pageSize));
  return (
    <div data-testid="research-pager">
      <SubTableFooter
        firstShown={first}
        lastShown={last}
        total={data.totalMatching}
        page={data.page}
        totalPages={totalPages}
        onPrevious={() => filters.setPage(data.page - 1)}
        onNext={() => filters.setPage(data.page + 1)}
        pageSizeOptions={RESEARCH_PAGE_SIZES}
        pageSize={filters.values.rSize}
        onPageSizeChange={(size) => filters.set("rSize", size as ResearchPageSize)}
      />
    </div>
  );
}

/**
 * What the applied refinement is hiding, and the one click that brings it back.
 *
 * <p>🔴 <b>RF1 — NOTHING MOVES; THINGS DISAPPEAR AND COME BACK.</b> A
 * refinement re-scores and filters; it never reorders. The wire order is still
 * the render order (RC4), and this line is what makes the rows that are NOT on
 * screen legible — twenty-eight cards missing with nothing saying why is a list
 * a specialist stops trusting.</p>
 *
 * <p>🔴 <b>DECISION 4 — ONE LINE, ONE CLICK, AND NEVER A DELETE.</b> No
 * confirm, no destructive styling: the hidden candidates are on the record
 * exactly as they were, and showing them again takes the lens off rather than
 * changing anything about them.</p>
 *
 * <p>🔴 <b>THE CONSEQUENCE IS STATED BEFORE THE PRESS.</b> Taking the lens off
 * also moves what Pull more would buy (decision 6), and a control that changed
 * the pull target silently would be a surprise a specialist finds out about by
 * spending records on the wrong search. Same rule, same reason, as the revert
 * control's own `consequence` line.</p>
 *
 * <p>Read-only for somebody who cannot edit the buyer: the FACT of the lens is
 * what makes the list readable and belongs to every reader, while the control
 * that changes it is hidden rather than disabled — the module convention.</p>
 */
function RefinementLens({
  lens,
  buyerLeadId,
  runId,
  canEdit,
}: {
  lens: BuyerResearchRefinementDto;
  buyerLeadId: string;
  runId: string;
  canEdit: boolean;
}) {
  const copy = researchCopy.results;
  const clear = useClearRefinement(buyerLeadId);
  const [error, setError] = useState<string | null>(null);

  // 🔴 THE LENS IS APPLIED BY THE SERVER, BEFORE EVERY OTHER FILTER.
  // `visibleCount` is how many rows the refinement kept; the list below is the
  // server's page of THAT set, narrowed further by whatever the filter bar
  // says. So the two numbers are different claims and neither has to equal the
  // other: this line counts what the refinement kept, and the count line under
  // the bar counts what also cleared the filters. The per-candidate outcomes
  // never cross the wire, so the client could not apply the lens itself even
  // if it wanted to — the list read is the refined set, from the server.
  const somethingHidden = lens.hiddenCount > 0;
  if (!somethingHidden && lens.promotedCount === 0) return null;

  return (
    <div className="grid gap-space-1" data-testid="research-refinement-lens">
      {somethingHidden && (
        <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-refinement-hidden">
          {copy.refinementHidden(lens.visibleCount, lens.hiddenCount, lens.text)}
          {canEdit && (
            <>
              {" "}
              <Button
                type="button"
                variant="text"
                size="sm"
                loading={clear.isPending}
                onClick={() => {
                  setError(null);
                  clear.mutate(
                    { runId, refinementId: lens.id },
                    // The same refusal vocabulary the panel renders — one map,
                    // one home. A clear refused for a reason this surface
                    // answered in its own words would be two answers to one
                    // question, and the quiet one would go stale.
                    { onError: (e: unknown) => setError(refinementRefusalMessage(e)) },
                  );
                }}
                data-testid="research-refinement-show-all"
              >
                {copy.refinementShowAll}
              </Button>
            </>
          )}
        </p>
      )}

      {somethingHidden && canEdit && (
        <p className="m-0 text-caption text-gray-mid break-words">
          {copy.refinementShowAllConsequence}
        </p>
      )}

      {/* RF2 — a promoted candidate was persisted-but-invisible before this
          refinement, matched it, and was SCORED before it was shown. Without
          this line a specialist reads a list that grew and assumes records were
          bought to grow it. */}
      {lens.promotedCount > 0 && (
        <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-refinement-promoted">
          {copy.refinementPromoted(lens.promotedCount)}
        </p>
      )}

      {error && (
        <p className="m-0 text-caption text-status-error" role="alert" data-testid="research-refinement-error">
          {error}
        </p>
      )}
    </div>
  );
}

/** The buyer's whole dismissal set, read-only, each with an Undismiss. */
function DismissalList({ buyerLeadId, canEdit }: { buyerLeadId: string; canEdit: boolean }) {
  const copy = researchCopy.results;
  const tz = useUserTimeZone();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const dismissals = useResearchDismissals(buyerLeadId, true);
  const undismiss = useUndismissCandidate(buyerLeadId);

  return (
    <section className="grid gap-space-2" data-testid="research-dismissals">
      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">
        {copy.dismissedHeading}
      </h3>
      {error && (
        <p className="m-0 text-caption text-status-error" role="alert">
          {error}
        </p>
      )}
      {dismissals.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : dismissals.isError || !dismissals.data ? (
        <InlineQueryError
          what="this buyer's dismissals"
          onRetry={() => void dismissals.refetch()}
          retrying={dismissals.isFetching}
        />
      ) : dismissals.data.length === 0 ? (
        <p className="m-0 text-small text-gray-mid">{copy.dismissedEmpty}</p>
      ) : (
        <ul className="m-0 p-0 list-none grid gap-space-1">
          {dismissals.data.map((d) => (
            <DismissalRow
              key={d.id}
              dismissal={d}
              tz={tz}
              canEdit={canEdit}
              pending={undismiss.isPending && undismiss.variables === d.id}
              onUndismiss={() => {
                setError(null);
                undismiss.mutate(d.id, {
                  onSuccess: () => toast.success(copy.undismissedToast),
                  onError: (e: unknown) => setError(extractErrorMessage(e)),
                });
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function DismissalRow({
  dismissal: d,
  tz,
  canEdit,
  pending,
  onUndismiss,
}: {
  dismissal: BuyerResearchDismissalDto;
  tz: string;
  canEdit: boolean;
  pending: boolean;
  onUndismiss: () => void;
}) {
  const copy = researchCopy.results;
  return (
    <li
      className="flex flex-wrap items-center justify-between gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
      data-testid="research-dismissal-row"
    >
      <div className="min-w-0 grid gap-[2px]">
        <span className="flex flex-wrap items-center gap-space-2 text-small text-onyx">
          {d.sourceUrlSnapshot ? (
            <a
              href={d.sourceUrlSnapshot}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-shadow hover:underline break-words"
            >
              {d.titleSnapshot || copy.untitled}
            </a>
          ) : (
            <span className="break-words">{d.titleSnapshot || copy.untitled}</span>
          )}
          {d.sourceKeySnapshot && <StatusBadge tone="neutral">{d.sourceKeySnapshot}</StatusBadge>}
        </span>
        <span className="text-caption text-gray-mid">
          <Timestamp value={d.dismissedAtUtc} tz={tz} /> {copy.dismissedBy(d.dismissedByName)}
          {d.reason && ` · ${d.reason}`}
        </span>
      </div>
      {canEdit && (
        <Button size="sm" variant="secondary" onClick={onUndismiss} loading={pending} data-testid="research-undismiss">
          {copy.undismiss}
        </Button>
      )}
    </li>
  );
}

/**
 * The rows this run bought, scored, and did not show.
 *
 * <p>🔴 <b>IT BUYS NOTHING, WHICH IS WHY IT IS NOT "PULL MORE".</b> The rows are
 * already paid for and already carry a fit score, their reasons and their
 * summary. Since R1 a batch shows every row it scores, so they exist only on a
 * run cut before R1 (a batch then showed 20 of the 40 it ranked) or after a
 * refinement's promotion scored rows the run never showed (the R21 ticket).</p>
 *
 * <p>🔴 <b>THE COUNT IS RUN-WIDE.</b> A run cut before R1 applied the cut to each
 * batch, so every figure here is the server's counted total; nothing is derived
 * from a configured cap, because a sentence built on "the other twenty" is wrong
 * for every run that was pulled. `researchCopy.results.withheldAvailable`
 * carries that in full.</p>
 *
 * <p>🔴 <b>THE CLIENT DECIDES NOTHING.</b> Whether the rest can be shown arrives
 * on `run.withheld`, from `BuyerResearchRevealEligibility` — the same expression
 * the endpoint re-runs against its own count before it writes. A second
 * predicate here would be free to offer a reveal the server then refuses.</p>
 */
function WithheldFooter({
  buyerLeadId,
  runId,
  withheld,
  canEdit,
}: {
  buyerLeadId: string;
  runId: string;
  withheld: BuyerResearchWithheldStateDto | null;
  canEdit: boolean;
}) {
  const copy = researchCopy.results;
  const reveal = useRevealWithheldResearch(buyerLeadId);
  const [error, setError] = useState<string | null>(null);
  // Held locally because the successful call invalidates the run read, so by
  // the time the confirmation renders, the count it is confirming is gone.
  const [revealed, setRevealed] = useState<number | null>(null);

  const nothingHeld = withheld == null || withheld.count <= 0;

  // 🔴 The confirmation is gated on there being nothing held BACK, not merely on
  // having revealed once. A later pull can withhold a fresh batch's remainder,
  // and a confirmation that outlived its own count would sit where that new
  // offer belongs and hide it — with no effect, no reset and no second piece of
  // state to keep in step, because a non-zero count supersedes it by itself.
  if (revealed != null && nothingHeld) {
    return (
      <p
        className="m-0 text-caption text-gray-mid break-words"
        role="status"
        data-testid="research-withheld-revealed"
      >
        {copy.withheldRevealed(revealed)}
      </p>
    );
  }

  if (nothingHeld) return null;

  return (
    <section
      className="grid gap-space-2"
      data-testid="research-withheld"
      data-can-reveal={withheld.canReveal}
    >
      {error && (
        <p
          className="m-0 text-caption text-status-error"
          role="alert"
          data-testid="research-withheld-error"
        >
          {error}
        </p>
      )}

      {/* ONE element for the sentence in both states, so a reader and a test are
          looking at the same place whichever it is. The cost clause belongs to
          the offer — it says what pressing costs, and in the other state there
          is nothing to press. */}
      <p
        className="m-0 text-caption text-gray-mid break-words"
        data-testid="research-withheld-available"
      >
        {withheld.canReveal
          ? `${copy.withheldAvailable(withheld.count)} ${copy.withheldCost}`
          : copy.withheldNotYet(withheld.count)}
      </p>

      {/* §6.1, and the standard's rule that a surface must never render a
          control that cannot do what it appears to do: while the run is still
          working the rows are real and unreachable, so the count is prose and no
          button is drawn. A disabled button is the same claim with a dead
          affordance attached to it. */}
      {withheld.canReveal && canEdit && (
        <div>
          <Button
            size="sm"
            variant="secondary"
            loading={reveal.isPending}
            onClick={() => {
              setError(null);
              const asked = withheld.count;
              reveal.mutate(runId, {
                onSuccess: () => setRevealed(asked),
                onError: (e: unknown) => setError(extractErrorMessage(e)),
              });
            }}
            data-testid="research-withheld-show"
          >
            {reveal.isPending ? copy.withheldPending : copy.withheldAction}
          </Button>
        </div>
      )}
    </section>
  );
}
